import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapPinIcon,
  HeartIcon,
  ShoppingCartIcon,
  TagIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";

const ListingCard = ({
  item,
  isFavorite,
  onToggleFavorite,
  onAddToCart,
  onBookService,
  showCompany = true,
  detailsPath,
}) => {
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(price || 0));
  };

  const normalizedType = String(item?.type || "")
    .toLowerCase()
    .trim();

  const isService =
    normalizedType === "service" ||
    normalizedType === "services" ||
    item?.advance_booking_days !== undefined ||
    item?.available_days !== undefined ||
    item?.assigned_employees !== undefined ||
    item?.buffer_time_before !== undefined ||
    item?.buffer_time_after !== undefined;

  const itemImage = item?.image_url || item?.image || "";

  const companyPath = item?.company_id ? `/companies/${item.company_id}` : null;

  const handleOpenDetails = () => {
    if (!detailsPath) return;

    navigate(detailsPath);
  };

  const handleFavoriteClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (onToggleFavorite && item?.id) {
      onToggleFavorite(item.id);
    }
  };

  const handleAddToCartClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
  
    if (isService) {
      if (onBookService && item) {
        onBookService(item);
      }
  
      return;
    }
  
    if (onAddToCart && item) {
      onAddToCart(item);
    }
  };

  return (
    <div className="bg-white rounded-[28px] border border-zinc-100 shadow-sm hover:shadow-xl hover:shadow-zinc-200/50 transition-all overflow-hidden flex flex-col group select-none">
      {/* Image Section */}
      <div className="relative h-52 bg-zinc-100 overflow-hidden">
        <button
          type="button"
          onClick={handleOpenDetails}
          disabled={!detailsPath}
          className={`block w-full h-full appearance-none border-0 p-0 text-left bg-transparent ${
            detailsPath ? "cursor-pointer" : "cursor-default"
          }`}
          aria-label={
            detailsPath ? `Открыть подробности ${item?.name || ""}` : undefined
          }
        >
          {itemImage ? (
            <img
              src={itemImage}
              alt={item?.name || "Listing image"}
              draggable="false"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-50 pointer-events-none">
              <TagIcon className="w-10 h-10 text-zinc-200" />
            </div>
          )}
        </button>

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
          <span className="bg-white/90 backdrop-blur-md text-zinc-900 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider shadow-sm">
            {isService ? "Услуга" : "Товар"}
          </span>
        </div>

        {/* Favorite Button */}
        <button
          type="button"
          onClick={handleFavoriteClick}
          className="absolute top-4 right-4 p-3 bg-white/90 backdrop-blur-md rounded-2xl shadow-sm hover:bg-white transition-all cursor-pointer"
          aria-label={
            isFavorite ? "Убрать из избранного" : "Добавить в избранное"
          }
        >
          {isFavorite ? (
            <HeartIconSolid className="h-5 w-5 text-rose-500" />
          ) : (
            <HeartIcon className="h-5 w-5 text-zinc-400" />
          )}
        </button>
      </div>

      {/* Content Section */}
      <div className="p-5 flex-1 flex flex-col">
        {detailsPath ? (
          <button
            type="button"
            onClick={handleOpenDetails}
            className="appearance-none border-0 bg-transparent p-0 text-left text-lg font-black text-zinc-900 line-clamp-1 mb-1 hover:text-orange-600 transition-colors cursor-pointer"
          >
            {item?.name}
          </button>
        ) : (
          <h3 className="text-lg font-black text-zinc-900 line-clamp-1 mb-1 group-hover:text-orange-600 transition-colors">
            {item?.name}
          </h3>
        )}

        {showCompany && item?.company && (
          <>
            {companyPath ? (
              <Link
                to={companyPath}
                className="text-xs font-bold text-zinc-400 mb-3 hover:text-orange-500 transition-colors inline-block"
              >
                {item.company}
              </Link>
            ) : (
              <span className="text-xs font-bold text-zinc-400 mb-3 inline-block">
                {item.company}
              </span>
            )}
          </>
        )}

        {!showCompany && item?.duration && (
          <div className="flex items-center gap-1 text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-3">
            <ClockIcon className="w-3.5 h-3.5" />
            {item.duration} мин
          </div>
        )}

        <p className="text-zinc-500 text-sm font-medium leading-relaxed mb-6 line-clamp-2">
          {item?.description || "Нет описания"}
        </p>

        {/* Footer Section */}
        <div className="pt-4 border-t border-zinc-100 flex items-center justify-between mt-auto">
          <div className="flex flex-col min-w-0">
            <span className="text-xl font-black text-zinc-900">
              {formatPrice(item?.price)}
            </span>

            <div className="flex items-center gap-1 text-[10px] text-zinc-400 font-bold uppercase mt-0.5">
              <MapPinIcon className="h-3 w-3 flex-shrink-0" />

              <span className="truncate max-w-[100px]">
                {item?.location || "—"}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleAddToCartClick}
            className={`${
              isService ? "px-4 py-3" : "p-3"
            } bg-zinc-900 text-white rounded-xl hover:bg-orange-600 transition-colors active:scale-90 shadow-lg shadow-zinc-900/10 cursor-pointer text-xs font-black uppercase tracking-wider`}
            aria-label={isService ? "Забронировать услугу" : "Добавить в корзину"}
          >
            {isService ? (
              "Book"
            ) : (
              <ShoppingCartIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
